import { type CSSProperties, type ReactNode } from 'react';
import { Colors } from '~/themes/colors';
import { Column } from '../Column';
import CustomSpinner from '../CustomSpinner';
import { Text } from '../Text';

interface IGridTableProps<T = unknown> {
  /** Array of data items to render in the table */
  data: T[];
  /** Function to render each row - receives item and returns ReactNode */
  renderRow: (item: T) => ReactNode;
  /** Array of column header titles */
  headerTitles: string[];
  /** Loading state - shows spinner when true */
  isLoading?: boolean;
  /** Additional styles for the header row */
  headerStyle?: CSSProperties;
}

/**
 * GridTable Component
 *
 * A reusable table component that renders data in a grid layout with dynamic headers,
 * custom row rendering, and optional loading state with spinner.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for office job invites display. Consider whether
 * this should be moved to a more specific location like:
 * - components/ProfessionalsHub/components/shared
 * - components/shared/Tables
 *
 * If this is truly only used for office job invites context, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <GridTable
 *   data={jobs}
 *   renderRow={(item) => <CustomCard job={item} />}
 *   headerTitles={['Date', 'Profession', 'Time']}
 *   isLoading={false}
 *   headerStyle={{ marginLeft: 80 }}
 * />
 * ```
 */
export const GridTable = <T,>({
  data,
  renderRow,
  headerTitles,
  isLoading = false,
  headerStyle = {},
}: IGridTableProps<T>) => (
  <Column>
    <div
      style={{
        width: '100%',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${headerTitles.length}, 2fr) 1fr`,
          ...headerStyle,
        }}
      >
        {headerTitles?.map((title) => (
          <Text
            bold
            color={Colors.primary_500}
            fontSize={17}
            key={title}
            paddingBottom={8}
            textAlign="center"
          >
            {title}
          </Text>
        ))}
        <Text cursor="default" opacity={0}>
          Action
        </Text>
      </div>
      <Column gap={8} marginTop={12}>
        {data.map((item, index) => (
          <div key={index}>{renderRow(item)}</div>
        ))}
      </Column>
    </div>

    {isLoading && <CustomSpinner isLoading size={32} style={{ marginTop: 20 }} />}
  </Column>
);
